import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { EmailAddress } from 'src/app/models/email-address';
import { Rights } from 'src/app/models/enums/rights';
import { IEmailAddress } from 'src/app/models/interfaces/email-address.interface';
import { EmailAddressesService } from 'src/app/services/emailAddress.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { EmailAddressDetailContext } from './email-address-detail-context';
import { EmailAddressDetailComponent } from './email-address-detail/email-address-detail.component';
import { EmailAddressesListComponent } from './email-addresses-list/email-addresses-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-email-addresses',
    templateUrl: './email-addresses.component.html',
    styleUrls: ['./email-addresses.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, EmailAddressesListComponent, DetailHeaderComponent, EmailAddressDetailComponent],
})
export class EmailAddressesComponent extends DefaultManagementComponent<IEmailAddress, EmailAddress, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faEmailAddress = faAddressBook;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;
    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre emailAddresses.
     */
    public defaultContext: EmailAddressDetailContext = new EmailAddressDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param EmailAddressesService : Service métier utilisé pour gérer les emailAddresses.
     */
    constructor(
        protected override notificationService: NotificationService,
        private EmailAddressesService: EmailAddressesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.EmailAddressCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.EmailAddressEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un emailAddress.
     * @param emailAddress EmailAddress selectionné.
     */
    public onEmailAddressSelected(emailAddress: EmailAddress) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadEmailAddress(emailAddress.id).subscribe(
            (x) => {
                this.refresh(EmailAddress.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'adresse mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param emailAddress emailAddress sélectionné.
     */
    public onEmailAddressDisplayed(emailAddress: EmailAddress) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadEmailAddress(emailAddress.id).subscribe(
            (x) => {
                this.refresh(EmailAddress.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'adresse mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param emailAddress emailAddress sélectionné.
     */
    public onEmailAddressEdited(emailAddress: EmailAddress) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadEmailAddress(emailAddress.id).subscribe(
            (x) => {
                this.refresh(EmailAddress.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer l'adresse mail." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onEmailAddressCreated() {
        this.displayForm(EmailAddress.createFormCopy(new EmailAddress()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param emailAddress emailAddress sélectionné.
     */
    public onEmailAddressDuplicated(emailAddress: EmailAddress) {
        let duplicatedEmailAddress = EmailAddress.createFormCopy(emailAddress);
        duplicatedEmailAddress.id = null;
        this.displayForm(duplicatedEmailAddress, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param EmailAddress : Valeur du emailAddress retourné par le formulaire.
     */
    public emailAddressUpdated(emailAddress: EmailAddress): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.EmailAddressesService.update(this.context.editedItemId, emailAddress).subscribe(
                (x) => {
                    this.refreshWithNotification(EmailAddress.createFormCopy(x), "L'adresse mail a été mise à jour", FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'adresse mail. " + error.reasonPhrase);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.EmailAddressesService.add(emailAddress).subscribe(
                (x) => {
                    this.refreshWithNotification(EmailAddress.createFormCopy(x), "L'adresse mail a été créé", FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer l'adresse mail." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param emailAddress emailAddress sélectionné.
     */
    public onEmailAddressDeleted(emailAddress: EmailAddress) {
        this.spinner.show('main-spinner');
        // Suppression effective du emailAddress.
        this.EmailAddressesService.delete(emailAddress.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'adresse mail a été supprimée");
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de supprimer l'adresse mail. " + error.reasonPhrase);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onEmailAddressesExported() {
        this.createContext();
        this.itemsList.export('EmailAddresses.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onEmailAddressesImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onEmailAddressesHelped() {
        this.itemsList.displayHelpDialog('emailAddresses');
    }

    /**
     * Charge les données d'un emailAddress.
     * @param EmailAddressId : Id du emailAddress à charger.
     */
    public loadEmailAddress(emailAddressId: number): Observable<EmailAddress> {
        return this.EmailAddressesService.get(emailAddressId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param EmailAddress : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(EmailAddress: IEmailAddress): EmailAddressDetailContext {
        let context = this.createContext();
        context.formContext.item = EmailAddress;
        context.editedItemId = EmailAddress.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): EmailAddressDetailContext {
        let context = new EmailAddressDetailContext();
        return context;
    }
}
